import { Button } from '@nesso/ui/primitives/button';
import {
    Item,
    ItemActions,
    ItemContent,
    ItemGroup,
    ItemMedia,
    ItemTitle,
} from '@nesso/ui/primitives/item';

import type { SelectedClient } from '@/entities/client';
import type { GetClientActivityOutput } from '@/entities/session';
import { openScheduleSession } from '@/features/session/schedule-session';
import { DotSeparatedText } from '@/shared/ui/text/dot-separated-text';

import { formatActivitySummary } from '../../../lib/format-activity-summary';
import { formatFirstScheduledSession } from '../../../lib/format-first-scheduled-session';
import { ClientDetailsChartActivityEntry } from './client-details-chart-activity/client-details-chart-activity-entry';

type Props = {
    client: SelectedClient | undefined;
    activity: GetClientActivityOutput | undefined;
    isError: boolean;
};

const EMPTY_ACTIVITY: GetClientActivityOutput = {
    completedCount: 0,
    cancelledCount: 0,
    recent: [],
    nextScheduledDate: null,
};

export function ClientDetailsChartActivity({ client, activity = EMPTY_ACTIVITY, isError }: Props) {
    if (isError) {
        return (
            <Item variant="muted" size="sm" alignment="center">
                <ItemContent>
                    <ItemTitle>Couldn't load chart activity</ItemTitle>
                </ItemContent>
            </Item>
        );
    }

    const nextScheduledDate =
        activity.recent.length === 0 && activity.nextScheduledDate
            ? activity.nextScheduledDate
            : null;

    return (
        <div className="flex flex-col gap-5 pb-2">
            <DotSeparatedText
                items={formatActivitySummary(activity.completedCount, activity.cancelledCount)}
            />
            {activity.recent.length === 0 ? (
                nextScheduledDate ? (
                    <Item variant="muted" alignment="center">
                        <ItemMedia variant="tile">
                            <span className="text-xxs leading-none font-bold uppercase">
                                {new Date(nextScheduledDate).toLocaleString('en-US', {
                                    month: 'short',
                                })}
                            </span>
                            <span className="text-base leading-none font-bold">
                                {new Date(nextScheduledDate).getDate()}
                            </span>
                        </ItemMedia>
                        <ItemContent>
                            <ItemTitle>
                                First session scheduled for{' '}
                                {formatFirstScheduledSession(nextScheduledDate)}
                            </ItemTitle>
                        </ItemContent>
                    </Item>
                ) : (
                    <Item variant="muted" size="sm" alignment="center">
                        <ItemContent>
                            <ItemTitle>No sessions scheduled yet</ItemTitle>
                        </ItemContent>
                        <ItemActions>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={!client}
                                onClick={() => client && openScheduleSession(client)}
                            >
                                Schedule Session
                            </Button>
                        </ItemActions>
                    </Item>
                )
            ) : (
                <ItemGroup className="gap-3">
                    {activity.recent.map((item) => (
                        <ClientDetailsChartActivityEntry key={item.id} item={item} />
                    ))}
                </ItemGroup>
            )}
        </div>
    );
}
